import React, { useState, useEffect } from "react";
import { BiArrowBack } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import CampaignHeader from "../component/Campaign/CampaignHeader";
import AudienceSection from "../component/Campaign/AudienceSection";
import MessageSection from "../component/Campaign/MessageSection";
import NextStepsSection from "../component/Campaign/NextStepsSection";
import ScheduleSection from "../component/Campaign/ScheduleSection";
import GoLiveSection from "../component/Campaign/GoLiveSection";
import Modals from "../component/Campaign/Modals";
import ToastNotifications from "../component/Campaign/ToastNotifications";

const Dropdown = () => {
  // State declarations remain the same
  const [showOptOutModal, setShowOptOutModal] = useState(false);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [showListMessageModal, setShowListMessageModal] = useState(false);
  const [showCustomReplyModal, setShowCustomReplyModal] = useState(false);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [errors, setErrors] = useState({});
  const [showErrorPanel, setShowErrorPanel] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [templateMap, setTemplateMap] = useState({});
  const [scheduleOption, setScheduleOption] = useState("immediately");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [isMediaTemplate, setIsMediaTemplate] = useState(false);
  const [variableValues, setVariableValues] = useState({});
  const [buttonValues, setButtonValues] = useState({});
  const [selectedTemplateData, setSelectedTemplateData] = useState(null);
  const [mediaFile, setMediaFile] = useState(null);
  const [optOutConfig, setOptOutConfig] = useState(null);
  const [productsConfig, setProductsConfig] = useState(null);
  const [listMessageConfig, setListMessageConfig] = useState(null);
  const [customReplyConfig, setCustomReplyConfig] = useState(null);
  const [workflowConfig, setWorkflowConfig] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    campaignName: "",
    messageTemplate: "",
    contacts: "",
    scheduleDate: "",
    scheduleTime: "",
  });

  const [enableRecurrence, setEnableRecurrence] = useState(false);
  const [recurrencePattern, setRecurrencePattern] = useState("daily");
  const [recurrenceEndOption, setRecurrenceEndOption] = useState("noEnd");
  const [recurrenceEndAfter, setRecurrenceEndAfter] = useState(1);
  const [recurrenceEndBy, setRecurrenceEndBy] = useState("");
  const [weeklyDays, setWeeklyDays] = useState([]);
  const [monthlyOption, setMonthlyOption] = useState("dayOfMonth");
  const [monthlyDay, setMonthlyDay] = useState(1);
  const [yearlyOption, setYearlyOption] = useState("specificDate");
  const [recurrenceRule, setRecurrenceRule] = useState("all");

  const [selectedSegments, setSelectedSegments] = useState([]);
  const [segmentUsers, setSegmentUsers] = useState([]);
  const [segments, setSegments] = useState([]);

  const tenantName = localStorage.getItem("tenantName");

  useEffect(() => {
    // Fetch WhatsApp templates
    const fetchWhatsappTemplates = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/templates/templates`,
          {
            method: "GET",
            headers: { "x-email": localStorage.getItem("tenantEmail") },
          }
        );
        const data = await response.json();

        const mappedTemplates = data.data.map((item) => {
          const buttons =
            item.components.find((c) => c.type === "BUTTONS")?.buttons || [];
          return {
            id: item.id,
            name: item.name,
            components: item.components,
            language: item.language,
            category: item.category,
            status: item.status,
            variables:
              item.components
                .find((c) => c.type === "BODY")
                ?.text?.match(/{{\d+}}/g) || [],
            dynamicButtons: buttons.filter(
              (btn) => btn.type === "PHONE_NUMBER" || btn.type === "COPY_CODE"
            ),
            allButtons: buttons,
          };
        });

        setWhatsappTemplates(mappedTemplates);
        const map = {};
        mappedTemplates.forEach((tpl) => {
          map[tpl.name] = {
            id: tpl.id,
            components: tpl.components,
            variables: tpl.variables,
            dynamicButtons: tpl.dynamicButtons,
            allButtons: tpl.allButtons,
          };
        });
        setTemplateMap(map);
      } catch (error) {
        console.error("Error fetching WhatsApp templates:", error);
        setSubmitError("Failed to load templates.");
      }
    };

    // Fetch users
    const fetchUsers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/users`,
          { headers: { "x-email": localStorage.getItem("tenantEmail") } }
        );
        const data = await response.json();
        setUsers(data);
      } catch (error) {
        console.error("Failed to fetch users:", error);
      }
    };
    fetchWhatsappTemplates();
    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchSegments = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/segments`,
          { headers: { "x-email": localStorage.getItem("tenantEmail") } }
        );
        const data = await response.json();
        if (data.data && Array.isArray(data.data)) {
          setSegments(data.data);
        }
      } catch (error) {
        console.error("Failed to fetch segments:", error);
      }
    };
    fetchSegments();
  }, []);

  // Add this useEffect to fetch segment users when segments change
  useEffect(() => {
    const fetchSegmentUsers = async () => {
      if (selectedSegments.length === 0) {
        setSegmentUsers([]);
        return;
      }

      try {
        const allUsers = new Map(); // Use Map to avoid duplicates by phone number

        for (const segmentId of selectedSegments) {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/segments/${segmentId}/preview`,
            { headers: { "x-email": localStorage.getItem("tenantEmail") } }
          );
          const data = await response.json();

          if (data.data && data.data.users && Array.isArray(data.data.users)) {
            data.data.users.forEach((user) => {
              if (user.phoneNumber && !allUsers.has(user.phoneNumber)) {
                allUsers.set(user.phoneNumber, user);
              }
            });
          }
        }

        const uniqueUsers = Array.from(allUsers.values());
        setSegmentUsers(uniqueUsers);

        // 👇 ADD THIS
        setSelectedUsers((prev) => {
          const segmentUserIds = uniqueUsers.map((u) => u.userId);
          return Array.from(new Set([...prev, ...segmentUserIds]));
        });
      } catch (error) {
        console.error("Failed to fetch segment users:", error);
        setSegmentUsers([]);
      }
    };

    fetchSegmentUsers();
  }, [selectedSegments]);

  useEffect(() => {
    const segmentUserIds = segmentUsers.map((u) => u.userId);

    setSelectedUsers((prev) => {
      const manualOnly = prev.filter((id) => !segmentUserIds.includes(id));
      return [...manualOnly, ...segmentUserIds];
    });
  }, [segmentUsers]);

  useEffect(() => {
    let filtered = [...users];

    // Apply filters
    if (statusFilter !== "") {
      filtered = filtered.filter(
        (user) => String(user.statusCode) === statusFilter
      );
    }

    if (startDate || endDate) {
      filtered = filtered.filter((user) => {
        const createdDate = new Date(user.createdAt)
          .toISOString()
          .split("T")[0];
        return (
          (!startDate || createdDate >= startDate) &&
          (!endDate || createdDate <= endDate)
        );
      });
    }

    if (startTime || endTime) {
      filtered = filtered.filter((user) => {
        const time = new Date(user.createdAt).toTimeString().slice(0, 5);
        return (
          (!startTime || time >= startTime) && (!endTime || time <= endTime)
        );
      });
    }

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.userName.toLowerCase().includes(query) ||
          user.phoneNumber.toLowerCase().includes(query) ||
          user.emailId.toLowerCase().includes(query) ||
          (user.tag && user.tag.toLowerCase().includes(query))
      );
    }

    setFilteredUsers(filtered);

    // Update select all state
    if (filtered.length > 0) {
      const allSelected = filtered.every((user) =>
        selectedUsers.includes(user.userId)
      );
      setSelectAll(allSelected);
    } else {
      setSelectAll(false);
    }
  }, [
    statusFilter,
    startDate,
    endDate,
    startTime,
    endTime,
    users,
    searchQuery,
    selectedUsers,
  ]);

  const toggleDropdown = (index) => {
    setOpenDropdown(openDropdown === index ? null : index);
  };

  const toggleFilterPanel = () => {
    setShowFilters((prev) => !prev);
  };

  const handleMediaUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMediaFile(file);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOptionChange = (option) => {
    setScheduleOption(option);
    if (option === "immediately") {
      const now = new Date(Date.now() + 2000);
      const dateString = now.toISOString().split("T")[0];
      const timeString = now.toTimeString().split(" ")[0].slice(0, 5);
      setFormData((prev) => ({
        ...prev,
        scheduleDate: dateString,
        scheduleTime: timeString,
      }));
    } else if (option === "customDate") {
      setFormData((prev) => ({
        ...prev,
        scheduleDate: "",
        scheduleTime: "",
      }));
    }
  };

  const handleUserSelection = (e, userId) => {
    if (e.target.checked) {
      setSelectedUsers((prev) => [...prev, userId]);
    } else {
      setSelectedUsers((prev) => prev.filter((id) => id !== userId));
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allUserIds = filteredUsers.map((user) => user.userId);
      setSelectedUsers(allUserIds);
      setSelectAll(true);
    } else {
      setSelectedUsers([]);
      setSelectAll(false);
    }
  };

  const handleVariableChange = (e, varName) => {
    setVariableValues({
      ...variableValues,
      [varName]: e.target.value,
    });
  };

  const handleButtonChange = (e, btnName) => {
    setButtonValues({
      ...buttonValues,
      [btnName]: e.target.value,
    });
  };

  const handleTemplateSelect = (templateName) => {
    setFormData({
      ...formData,
      messageTemplate: templateName,
    });

    const template = whatsappTemplates.find((t) => t.name === templateName);
    if (template) {
      setSelectedTemplateData(template);
      setMediaFile(null);

      // Check for media header
      const hasMediaHeader = template.components.some(
        (comp) =>
          comp.type === "HEADER" &&
          ["IMAGE", "VIDEO", "DOCUMENT"].includes(comp.format)
      );
      setIsMediaTemplate(hasMediaHeader);

      // Initialize variable values
      const vars = {};
      template.variables.forEach((v, i) => {
        vars[`var${i + 1}`] = "";
      });
      setVariableValues(vars);

      // Initialize button values only for PHONE_NUMBER and COPY_CODE
      const btns = {};
      template.dynamicButtons.forEach((btn, i) => {
        if (btn.type === "PHONE_NUMBER") {
          btns[`phone_btn_${i}`] = btn.phone_number || "";
        }
        if (btn.type === "COPY_CODE") {
          btns[`copy_btn_${i}_text`] = btn.text || "";
          btns[`copy_btn_${i}_code`] = "";
        }
      });
      setButtonValues(btns);
    } else {
      setIsMediaTemplate(false);
      setSelectedTemplateData(null);
      setMediaFile(null);
    }
  };

  const closeErrorPanel = () => {
    setShowErrorPanel(false);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.campaignName)
      newErrors.campaignName = "Campaign name is required";
    if (!formData.messageTemplate)
      newErrors.messageTemplate = "Message template is required";

    // Check if ANY audience selection method is used
    const hasManualContacts =
      formData.contacts && formData.contacts.trim().length > 0;
    const hasSelectedUsers = selectedUsers.length > 0;
    const hasSegmentUsers = segmentUsers.length > 0;

    if (!hasManualContacts && !hasSelectedUsers && !hasSegmentUsers) {
      newErrors.contacts = "Select at least one contact, user, or segment";
    }
    if (scheduleOption === "customDate") {
      if (!formData.scheduleDate)
        newErrors.scheduleDate = "Schedule date is required";
      if (!formData.scheduleTime)
        newErrors.scheduleTime = "Schedule time is required";
    }
    setErrors(newErrors);

    // Show error panel if there are errors
    if (Object.keys(newErrors).length > 0) {
      setShowErrorPanel(true);
      // Scroll to the top to show the error panel
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (validate()) {
      try {
        // Prepare contacts
        const contactArray = formData.contacts
          .split(",")
          .map((c) => c.trim())
          .filter((c) => c);
        const selectedUserContacts = users
          .filter((user) => selectedUsers.includes(user.userId))
          .map((user) => user.phoneNumber);
        const segmentUserContacts = segmentUsers.map(
          (user) => user.phoneNumber
        );
        const allContacts = [
          ...new Set([
            ...contactArray,
            ...selectedUserContacts,
            ...segmentUserContacts,
          ]),
        ];

        // Prepare segment data for backend
        const segmentData =
          selectedSegments.length > 0
            ? {
                segmentIds: selectedSegments,
                segmentUserCount: segmentUsers.length,
              }
            : null;

        // Prepare schedule
        const scheduleISO = new Date(
          `${formData.scheduleDate}T${formData.scheduleTime}`
        ).toISOString();

        // Prepare recurrence data if enabled
        const recurrence = enableRecurrence
          ? Object.fromEntries(
              Object.entries({
                pattern: recurrencePattern,
                interval: 1,
                daysOfWeek:
                  recurrencePattern === "weekly" ? weeklyDays : undefined,
                monthlyOption:
                  recurrencePattern === "monthly" ? monthlyOption : undefined,
                monthlyDay:
                  recurrencePattern === "monthly" &&
                  monthlyOption === "dayOfMonth"
                    ? monthlyDay
                    : undefined,
                yearlyOption:
                  recurrencePattern === "yearly" ? yearlyOption : undefined,
                endOption: recurrenceEndOption,
                endAfter:
                  recurrenceEndOption === "endAfter"
                    ? recurrenceEndAfter
                    : undefined,
                endBy:
                  recurrenceEndOption === "endBy" ? recurrenceEndBy : undefined,
              }).filter(([_, v]) => v !== undefined)
            )
          : null;

        // Prepare template configuration
        const templateConfig = {
          id: templateMap[formData.messageTemplate].id,
          variables: variableValues,
          buttons: [],
        };

        // Process dynamic buttons
        selectedTemplateData.dynamicButtons.forEach((btn, i) => {
          const buttonConfig = { type: btn.type };

          if (btn.type === "PHONE_NUMBER") {
            buttonConfig.text = buttonValues[`phone_btn_${i}_text`] || btn.text;
            buttonConfig.phone_number =
              buttonValues[`phone_btn_${i}_number`] || "";
          } else if (btn.type === "COPY_CODE") {
            buttonConfig.text = buttonValues[`copy_btn_${i}_text`] || btn.text;
            buttonConfig.code = buttonValues[`copy_btn_${i}_code`] || "";
          }

          templateConfig.buttons.push(buttonConfig);
        });

        // Create FormData
        const formDataToSend = new FormData();
        formDataToSend.append("name", formData.campaignName);
        formDataToSend.append("templateId", templateConfig.id);
        formDataToSend.append("contacts", JSON.stringify(allContacts));
        formDataToSend.append("schedule", scheduleISO);
        formDataToSend.append("templateConfig", JSON.stringify(templateConfig));

        if (segmentData) {
          formDataToSend.append("segmentData", JSON.stringify(segmentData));
        }

        // Add next steps configurations if they exist
        if (optOutConfig) {
          formDataToSend.append("optOutConfig", JSON.stringify(optOutConfig));
        }
        if (productsConfig) {
          formDataToSend.append(
            "productsConfig",
            JSON.stringify(productsConfig)
          );
        }
        if (listMessageConfig) {
          formDataToSend.append(
            "listMessageConfig",
            JSON.stringify(listMessageConfig)
          );
        }
        if (customReplyConfig) {
          formDataToSend.append(
            "customReplyConfig",
            JSON.stringify(customReplyConfig)
          );
        }
        if (workflowConfig) {
          formDataToSend.append(
            "workflowConfig",
            JSON.stringify(workflowConfig)
          );
        }

        // Add media file if exists
        if (isMediaTemplate && mediaFile) {
          formDataToSend.append("media", mediaFile);
        }

        // Add recurrence to form data
        if (recurrence) {
          formDataToSend.append("recurrenceRule", recurrenceRule);

          formDataToSend.append(
            "recurrencePattern",
            JSON.stringify({
              type: recurrence.pattern,
              interval: 1,
              daysOfWeek: recurrence.daysOfWeek,
              monthlyOption: recurrence.monthlyOption,
              monthlyDay: recurrence.monthlyDay,
              yearlyOption: recurrence.yearlyOption,
            })
          );

          formDataToSend.append(
            "recurrenceEnd",
            JSON.stringify({
              type: recurrence.endOption,
              occurrences: recurrence.endAfter,
              endDate: recurrence.endBy,
            })
          );

          formDataToSend.append("isRecurring", true);
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/campaigns/create`,
          {
            method: "POST",
            body: formDataToSend,
            headers: {
              "x-email": localStorage.getItem("tenantEmail"),
            },
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || "Failed to create campaign");
        }

        // Reset form on success
        setFormData({
          campaignName: "",
          messageTemplate: "",
          contacts: "",
          scheduleDate: "",
          scheduleTime: "",
        });
        setSelectedUsers([]);
        setVariableValues({});
        setButtonValues({});
        setMediaFile(null);
        setIsMediaTemplate(false);
        setSelectedTemplateData(null);

        // Show success message
        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 3000);
      } catch (error) {
        console.error("Error creating campaign:", error.message);
        setSubmitError(error.message);
      }
    }
  };

  const scrollToError = (fieldName) => {
    let elementId = "";

    switch (fieldName) {
      case "campaignName":
        elementId = "campaignName";
        break;
      case "messageTemplate":
        // Scroll to message section and open it
        setOpenDropdown(2);
        setTimeout(() => {
          document.querySelector(".template-selector")?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 300);
        return;
      case "contacts":
        // Scroll to audience section and open it
        setOpenDropdown(1);
        setTimeout(() => {
          document.querySelector(".contacts-input")?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 300);
        return;
      case "scheduleDate":
      case "scheduleTime":
        // Scroll to schedule section and open it
        setOpenDropdown(4);
        setTimeout(() => {
          document.querySelector(".schedule-date-input")?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 300);
        return;
      default:
        return;
    }

    if (elementId) {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
    }
  };

  return (
    <div
      className="p-3"
      style={{
        maxHeight: "calc(100vh - 64px)",
        overflowY: "auto",
        margin: "0 auto",
      }}
    >
      {/* Error Notification Panel */}
      {showErrorPanel && Object.keys(errors).length > 0 && (
        <div
          className="alert alert-danger alert-dismissible fade show mb-4"
          role="alert"
        >
          <div className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill me-2 fs-5"></i>
            <h6 className="mb-0 me-3">
              Please fix the following errors to continue:
            </h6>
            <button
              type="button"
              className="btn-close"
              onClick={closeErrorPanel}
              aria-label="Close"
            ></button>
          </div>
          <ul className="mb-0 mt-2 ps-4">
            {Object.entries(errors).map(([field, message]) => (
              <li key={field} className="mb-1">
                {message}{" "}
                <button
                  className="btn btn-link btn-sm p-0 align-baseline text-decoration-none"
                  onClick={() => scrollToError(field)}
                >
                  <i className="bi bi-arrow-right-circle me-1"></i>Go to field
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="page-header mb-2 d-flex gap-5 align-items-center">
        <div>
          <button
            onClick={() => navigate(`/${tenantName}/campaigns`)}
            className="btn btn-outline-primary back-btn d-flex align-items-center"
          >
            <BiArrowBack className="me-2" /> Back to Campaigns
          </button>
        </div>
        <div>
          <h4 className="page-title mt-1">Create New Campaign</h4>
          <p className="text-muted">
            Create and schedule your WhatsApp campaign messages
          </p>
        </div>
      </div>

      {/* Header Section */}
      <CampaignHeader
        formData={formData}
        errors={errors}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
      />

      {/* Step 1: Choose your Audience */}
      <AudienceSection
        openDropdown={openDropdown === 1}
        toggleDropdown={() => toggleDropdown(1)}
        filteredUsers={filteredUsers}
        selectedUsers={selectedUsers}
        handleUserSelection={handleUserSelection}
        handleSelectAll={handleSelectAll}
        selectAll={selectAll}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        startTime={startTime}
        setStartTime={setStartTime}
        endTime={endTime}
        setEndTime={setEndTime}
        showFilters={showFilters}
        toggleFilterPanel={toggleFilterPanel}
        errors={errors}
        formData={formData}
        handleChange={handleChange}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedSegments={selectedSegments}
        setSelectedSegments={setSelectedSegments}
        segmentUsers={segmentUsers}
        setSegmentUsers={setSegmentUsers}
        tenantEmail={localStorage.getItem("tenantEmail")}
      />

      {/* Step 2: Create your Message */}
      <MessageSection
        openDropdown={openDropdown === 2}
        toggleDropdown={() => toggleDropdown(2)}
        whatsappTemplates={whatsappTemplates}
        formData={formData}
        errors={errors}
        handleTemplateSelect={handleTemplateSelect}
        selectedTemplateData={selectedTemplateData}
        isMediaTemplate={isMediaTemplate}
        variableValues={variableValues}
        handleVariableChange={handleVariableChange}
        buttonValues={buttonValues}
        handleButtonChange={handleButtonChange}
        mediaFile={mediaFile}
        handleMediaUpload={handleMediaUpload}
        templateMap={templateMap}
      />

      {/* Step 3: Set next steps */}
      <NextStepsSection
        openDropdown={openDropdown === 3}
        toggleDropdown={() => toggleDropdown(3)}
        setShowOptOutModal={setShowOptOutModal}
        setShowProductsModal={setShowProductsModal}
        setShowListMessageModal={setShowListMessageModal}
        setShowCustomReplyModal={setShowCustomReplyModal}
        setShowWorkflowModal={setShowWorkflowModal}
        optOutConfig={optOutConfig}
        productsConfig={productsConfig}
        listMessageConfig={listMessageConfig}
        customReplyConfig={customReplyConfig}
        workflowConfig={workflowConfig}
      />

      {/* Step 4: Schedule your message */}
      <ScheduleSection
        openDropdown={openDropdown === 4}
        toggleDropdown={() => toggleDropdown(4)}
        scheduleOption={scheduleOption}
        handleOptionChange={handleOptionChange}
        formData={formData}
        errors={errors}
        handleChange={handleChange}
        enableRecurrence={enableRecurrence}
        setEnableRecurrence={setEnableRecurrence}
        recurrencePattern={recurrencePattern}
        setRecurrencePattern={setRecurrencePattern}
        weeklyDays={weeklyDays}
        setWeeklyDays={setWeeklyDays}
        monthlyOption={monthlyOption}
        setMonthlyOption={setMonthlyOption}
        monthlyDay={monthlyDay}
        setMonthlyDay={setMonthlyDay}
        yearlyOption={yearlyOption}
        setYearlyOption={setYearlyOption}
        recurrenceEndOption={recurrenceEndOption}
        setRecurrenceEndOption={setRecurrenceEndOption}
        recurrenceEndAfter={recurrenceEndAfter}
        setRecurrenceEndAfter={setRecurrenceEndAfter}
        recurrenceEndBy={recurrenceEndBy}
        setRecurrenceEndBy={setRecurrenceEndBy}
        recurrenceRule={recurrenceRule}
        setRecurrenceRule={setRecurrenceRule}
      />

      {/* Step 5: Set your message live */}
      <GoLiveSection
        openDropdown={openDropdown === 5}
        toggleDropdown={() => toggleDropdown(5)}
        handleSubmit={handleSubmit}
        selectedUsersCount={selectedUsers.length}
        contactsCount={
          formData.contacts.split(",").filter((c) => c.trim()).length
        }
        /** NEW DATA FOR SEND TEST **/
        selectedTemplateData={selectedTemplateData}
        templateId={templateMap[formData.messageTemplate]?.id}
        variableValues={variableValues}
        buttonValues={buttonValues}
        mediaFile={mediaFile}
        isMediaTemplate={isMediaTemplate}
        tenantEmail={localStorage.getItem("tenantEmail")}
        workflowConfig={workflowConfig}
        customReplyConfig={customReplyConfig}
        optOutConfig={optOutConfig}
        productsConfig={productsConfig}
        listMessageConfig={listMessageConfig}
      />

      {/* Modals */}
      <Modals
        showOptOutModal={showOptOutModal}
        setShowOptOutModal={setShowOptOutModal}
        showProductsModal={showProductsModal}
        setShowProductsModal={setShowProductsModal}
        showListMessageModal={showListMessageModal}
        setShowListMessageModal={setShowListMessageModal}
        showCustomReplyModal={showCustomReplyModal}
        setShowCustomReplyModal={setShowCustomReplyModal}
        showWorkflowModal={showWorkflowModal}
        setShowWorkflowModal={setShowWorkflowModal}
        setOptOutConfig={setOptOutConfig}
        setProductsConfig={setProductsConfig}
        setListMessageConfig={setListMessageConfig}
        setCustomReplyConfig={setCustomReplyConfig}
        setWorkflowConfig={setWorkflowConfig}
      />

      {/* Toast Notifications */}
      <ToastNotifications
        submitted={submitted}
        submitError={submitError}
        setSubmitted={setSubmitted}
        setSubmitError={setSubmitError}
      />
    </div>
  );
};

export default Dropdown;
